import React from 'react';
import { FiBarChart2, FiTrendingUp, FiUsers, FiCheckCircle, FiClock, FiAward, FiZap, FiShield } from 'react-icons/fi';

const InstitutionalKPIs = () => {
  const kpis = [
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Student Enrollment & Retention Rate',
      description: 'Track how many students are enrolling each year and how many are staying. Enrollment growth signals institutional health. Retention rate reveals how well you\'re serving students. Low retention is expensive—it costs significantly more to recruit new students than to retain existing ones.',
      whyItMatters: 'A 5% improvement in retention can increase revenue by 25-85%',
      howToTrack: 'Truyon tracks enrollment trends and retention rates in real-time, with cohort analysis and year-over-year comparisons.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiAward className="w-6 h-6" />,
      title: 'Academic Performance & Pass Rates',
      description: 'What percentage of your students are passing their courses? Which programs have the highest success rates? Which need intervention? Pass rates are the most direct measure of academic effectiveness. They reflect teaching quality, curriculum design, and student support.',
      whyItMatters: 'Low pass rates hurt reputation and accreditation, and signal systemic issues',
      howToTrack: 'Truyon provides real-time pass rate dashboards by program, department, and cohort, with trend analysis.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Student Engagement & Attendance',
      description: 'Attendance and engagement are leading indicators of student success. Students who attend regularly and participate actively are far more likely to succeed. Conversely, declining attendance is the strongest predictor of dropout. Track engagement to intervene early.',
      whyItMatters: 'Regular attendance is the strongest predictor of academic success',
      howToTrack: 'Truyon monitors attendance patterns in real-time and flags students who cross risk thresholds.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Admission Conversion Rate',
      description: 'How many inquiries become applications? How many applications become enrollments? Conversion rates reveal the effectiveness of your admission process. Low conversion may indicate friction in the application process, poor communication, or slow decision-making.',
      whyItMatters: 'Improving conversion by 10% can fill your seats faster and reduce admission costs',
      howToTrack: 'Truyon tracks the entire admission funnel—from inquiry to enrollment—and identifies drop-off points.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Faculty Performance & Workload',
      description: 'Faculty are the heart of education. Track teaching loads, student feedback, research output, and professional development. Overworked faculty produce lower quality education. Underworked faculty represent inefficiency. Balance is key.',
      whyItMatters: 'Faculty satisfaction and effectiveness directly impact student outcomes and institutional reputation',
      howToTrack: 'Truyon tracks faculty workload distribution, feedback scores, and performance metrics in real-time.',
      color: 'bg-purple-50'
    }
  ];

  const benefits = [
    {
      title: 'Real-Time Visibility',
      description: 'See your key metrics updated in real-time, not weeks after the fact.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Early Problem Detection',
      description: 'Spot declining metrics before they become serious issues. Intervene early.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Data-Driven Decisions',
      description: 'Replace intuition with evidence. Make decisions based on real data, not guesses.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Better Resource Allocation',
      description: 'Invest where it matters most. Allocate resources based on performance data.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Improved Institutional Performance',
      description: 'Track what matters, improve what\'s tracked. Continuous improvement driven by data.',
      icon: <FiTrendingUp className="w-5 h-5" />
    },
    {
      title: 'Stakeholder Confidence',
      description: 'Show trustees, parents, and accreditation bodies that you\'re on top of performance.',
      icon: <FiAward className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Educational leaders make decisions every day that shape the future of their institutions. They decide where to invest resources, which programs to expand, how to support struggling students, and how to improve quality. These decisions have consequences—for students, for faculty, and for the institution itself.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        But decisions are only as good as the information behind them. <strong className="text-slate-900">Without real-time visibility into key performance indicators, leaders are flying blind.</strong> They're making decisions based on intuition, incomplete data, or information that's weeks or months old.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here are 5 KPIs every educational leader should track in real-time—and why they matter.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">5%</div>
          <div className="text-sm text-slate-500">Retention improvement = 25-85% revenue increase</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">10%</div>
          <div className="text-sm text-slate-500">Conversion improvement fills seats faster</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">100%</div>
          <div className="text-sm text-slate-500">Visibility into what matters most</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">5 KPIs Every Educational Leader Should Track</h2>

      {/* KPIs List */}
      {kpis.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">💡 {item.whyItMatters}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>How Truyon Helps</span>
                </div>
                <p className="text-sm text-slate-700">{item.howToTrack}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Why Real-Time KPIs Matter</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Annual Reports vs. Real-Time Dashboards</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Annual Reports Only</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Data is weeks or months old</span></li>
              <li className="flex items-start gap-2">• <span>Problems identified too late</span></li>
              <li className="flex items-start gap-2">• <span>Reactive decision-making</span></li>
              <li className="flex items-start gap-2">• <span>Hard to track progress</span></li>
              <li className="flex items-start gap-2">• <span>Limited strategic visibility</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Real-Time Dashboard with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Data updated in real-time</span></li>
              <li className="flex items-start gap-2">• <span>Problems identified early</span></li>
              <li className="flex items-start gap-2">• <span>Proactive decision-making</span></li>
              <li className="flex items-start gap-2">• <span>Continuous progress tracking</span></li>
              <li className="flex items-start gap-2">• <span>Complete strategic visibility</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Dashboard in Action */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Dashboard in Action</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-xl p-3 text-center border border-indigo-200">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-xs">View</h4>
            <p className="text-xs text-slate-600">See all KPIs at a glance</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-200">
            <div className="text-2xl mb-1">🔍</div>
            <h4 className="font-bold text-slate-900 text-xs">Analyze</h4>
            <p className="text-xs text-slate-600">Drill down into trends</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-200">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-xs">Act</h4>
            <p className="text-xs text-slate-600">Make informed decisions</p>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for Institutional KPIs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Dashboards</h4>
            <p className="text-xs text-slate-600">View all KPIs in one place</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📈</div>
            <h4 className="font-bold text-slate-900 text-sm">Trend Analysis</h4>
            <p className="text-xs text-slate-600">Track performance over time</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔍</div>
            <h4 className="font-bold text-slate-900 text-sm">Drill-Down Capabilities</h4>
            <p className="text-xs text-slate-600">Explore details behind the numbers</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile Access</h4>
            <p className="text-xs text-slate-600">View KPIs from anywhere</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-sm">Actionable Insights</h4>
            <p className="text-xs text-slate-600">Data that drives decisions</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Secure Access</h4>
            <p className="text-xs text-slate-600">Role-based data visibility</p>
          </div>
        </div>
      </div>

      {/* The Leadership Perspective */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">What Leaders Say About Real-Time KPIs</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          "I used to wait for monthly reports to understand how we were doing. Now I can see everything in real-time. When enrollment started dropping in one program, I caught it in week 3 and we took action. We turned it around before it became a crisis."
        </p>
        <p className="text-amber-800 leading-relaxed">
          — Principal, Private Degree College
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Track What Matters with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Educational leaders can't afford to fly blind. Real-time visibility into key performance indicators is essential for making informed decisions, identifying problems early, and driving institutional improvement.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon provides the dashboards and analytics you need—enrollment trends, retention rates, academic performance, admission conversion, and faculty workload. All in real-time. All in one place.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for institutional KPIs.</strong> Track what matters. Make better decisions. Build a stronger institution.
        </p>
      </div>
    </>
  );
};

export default InstitutionalKPIs;